import React, { useState } from 'react';
import { GripVertical } from 'lucide-react';

/**
 * Renders a horizontal list of drag-to-reorder tabs.
 *
 * Props:
 *  - tabs:      ordered array of tab objects (each must have a unique `id`)
 *  - onReorder: (orderedIds: string[]) => void  — fired after a successful drop
 *  - renderTab: (tab, isDragging) => ReactNode  — renders the tab's inner button
 *  - className: classes for the wrapping <nav>
 */
export const DraggableTabs = ({ tabs, onReorder, renderTab, className = '' }) => {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const next = [...tabs];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    setDragIndex(null);
    setOverIndex(null);
    onReorder(next.map((t) => t.id));
  };

  return (
    <nav className={className} data-testid="draggable-tabs">
      {tabs.map((tab, i) => (
        <div
          key={tab.id}
          draggable
          onDragStart={(e) => {
            setDragIndex(i);
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (overIndex !== i) setOverIndex(i);
          }}
          onDragLeave={() => setOverIndex((prev) => (prev === i ? null : prev))}
          onDrop={() => handleDrop(i)}
          onDragEnd={() => {
            setDragIndex(null);
            setOverIndex(null);
          }}
          data-testid={`draggable-tab-${tab.id}`}
          className={`relative cursor-grab active:cursor-grabbing transition-all duration-150 ${
            dragIndex === i ? 'opacity-40' : ''
          } ${
            overIndex === i && dragIndex !== null && dragIndex !== i
              ? 'ring-2 ring-amber-400 ring-offset-1 rounded-lg'
              : ''
          }`}
          title="Drag to reorder"
        >
          {renderTab(tab, dragIndex === i)}
        </div>
      ))}
    </nav>
  );
};

export const DragHint = ({ className = '' }) => (
  <span
    className={`inline-flex items-center gap-1 text-xs text-gray-400 ${className}`}
    data-testid="drag-hint"
  >
    <GripVertical className="h-3 w-3" />
    Drag tabs to reorder
  </span>
);

export default DraggableTabs;
